import type { ChatCommandDescriptor, WorkflowDefinitionSummary, NodeTypeCategory, Skill } from '@/types'

export const CATEGORY_ORDER: NodeTypeCategory[] = ['AI', 'Development', 'Integration', 'Utilities']

export type SlashItem =
  | { type: 'workflow'; workflow: WorkflowDefinitionSummary }
  | { type: 'command'; command: ChatCommandDescriptor }
  | { type: 'skill'; skill: Skill }

function matchesFilter(filter: string, ...fields: string[]): boolean {
  if (!filter) return true
  const q = filter.toLowerCase()
  return fields.some((f) => f.toLowerCase().includes(q))
}

function matchesSkillCategory(filter: string): boolean {
  const q = filter.toLowerCase()
  return q.length > 0 && 'skills'.startsWith(q)
}

function dedupeSkillsByName(skills: Skill[]): Skill[] {
  const byName = new Map<string, Skill>()
  for (const skill of skills) {
    const key = skill.name.toLowerCase()
    const existing = byName.get(key)
    if (!existing || (existing.isSystem && !skill.isSystem)) byName.set(key, skill)
  }
  return [...byName.values()]
}

/**
 * Builds the single, ordered list of `/`-menu items — workflows, then commands grouped by
 * CATEGORY_ORDER, then skills — shared by ChatInput (keyboard nav + index math) and
 * SlashCommandPicker (rendering), so the two can never disagree on what index N means.
 */
export function buildSlashItems(
  workflows: WorkflowDefinitionSummary[],
  commands: ChatCommandDescriptor[],
  skills: Skill[],
  filter: string,
): SlashItem[] {
  const filteredWorkflows = workflows.filter((w) =>
    matchesFilter(filter, w.name, w.displayName, w.description ?? ''),
  )

  const filteredCommandsByCategory = CATEGORY_ORDER.flatMap((category) =>
    commands.filter((c) => c.category === category && matchesFilter(filter, c.name, c.displayName, c.description)),
  )

  const filteredSkills = dedupeSkillsByName(
    matchesSkillCategory(filter)
      ? skills
      : skills.filter((s) => matchesFilter(filter, s.name, s.displayName, s.description ?? '')),
  )

  return [
    ...filteredWorkflows.map((workflow): SlashItem => ({ type: 'workflow', workflow })),
    ...filteredCommandsByCategory.map((command): SlashItem => ({ type: 'command', command })),
    ...filteredSkills.map((skill): SlashItem => ({ type: 'skill', skill })),
  ]
}
